/* ══ ENUM LABELS ════════════════════════════════════════════
   Bilingual display labels for fixed-vocabulary enums whose stored
   value must remain stable English keys (filtering, comparisons,
   permission lookups all rely on them). Keep storage as keys; resolve
   labels at render through this map. Unknown values fall back to a
   capitalized version of the key. */

const ENUM_LABELS = {
  /* ── Staff roles (workspace-scoped, may extend) ── */
  staffRole: {
    en: { driver:"Driver", maid:"Maid", housekeeper:"Housekeeper", cook:"Cook",
          gardener:"Gardener", security:"Security", manager:"Manager",
          handyman:"Handyman", nanny:"Nanny", chauffeur:"Chauffeur",
          guard:"Guard", chef:"Chef" },
    ar: { driver:"سائق", maid:"عاملة منزلية", housekeeper:"مدبرة منزل", cook:"طاهي",
          gardener:"بستاني", security:"حارس أمن", manager:"مدير",
          handyman:"فنّي صيانة", nanny:"مربية", chauffeur:"سائق خاص",
          guard:"حارس", chef:"شيف" },
  },

  /* ── Maintenance categories ── */
  maintCategory: {
    en: { electrical:"Electrical", hvac:"HVAC", plumbing:"Plumbing",
          cleaning:"Cleaning", painting:"Painting", carpentry:"Carpentry",
          security:"Security", general:"General" },
    ar: { electrical:"كهرباء", hvac:"تكييف", plumbing:"سباكة",
          cleaning:"نظافة", painting:"دهانات", carpentry:"نجارة",
          security:"أمن", general:"عام" },
  },

  /* ── Facility issue kinds ── */
  issueKind: {
    en: { issue:"Issue", repair:"Repair", inspection:"Inspection", preventive:"Preventive" },
    ar: { issue:"مشكلة", repair:"إصلاح", inspection:"فحص", preventive:"وقائية" },
  },

  /* ── Fleet supplier service categories ── */
  fleetService: {
    en: { fuel_station:"Fuel Station", mechanic:"Mechanic", tyre_shop:"Tyre Shop",
          car_wash:"Car Wash", parts_dealer:"Parts Dealer", insurance:"Insurance",
          registration:"Registration", general:"General" },
    ar: { fuel_station:"محطة وقود", mechanic:"ميكانيكي", tyre_shop:"محل إطارات",
          car_wash:"غسيل سيارات", parts_dealer:"تاجر قطع غيار", insurance:"تأمين",
          registration:"تسجيل", general:"عام" },
  },

  /* ── Grocery supplier categories ── */
  groceryCategory: {
    en: { supermarket:"Supermarket", butcher:"Butcher", bakery:"Bakery",
          organic:"Organic", bulk:"Bulk", delivery_service:"Delivery Service",
          dairy:"Dairy", seafood:"Seafood", general:"General" },
    ar: { supermarket:"سوبر ماركت", butcher:"جزارة", bakery:"مخبز",
          organic:"عضوي", bulk:"بالجملة", delivery_service:"خدمة توصيل",
          dairy:"ألبان", seafood:"مأكولات بحرية", general:"عام" },
  },

  /* ── "Hats" (free-text owner contexts; common defaults bilingual) ── */
  hat: {
    en: { GM:"GM", Investor:"Investor", Developer:"Developer", Personal:"Personal",
          Family:"Family", "Private Office":"Private Office" },
    ar: { GM:"المدير العام", Investor:"المستثمر", Developer:"المطوّر", Personal:"شخصي",
          Family:"العائلة", "Private Office":"المكتب الخاص" },
  },

  /* ── Meal categories ── */
  mealCategory: {
    en: { breakfast:"Breakfast", lunch:"Lunch", dinner:"Dinner", snack:"Snack" },
    ar: { breakfast:"الفطور", lunch:"الغداء", dinner:"العشاء", snack:"وجبة خفيفة" },
  },

  /* ── Common course types (for menu planning) ── */
  course: {
    en: { Appetizer:"Appetizer", Soup:"Soup", Main:"Main", Side:"Side",
          Dessert:"Dessert", Salad:"Salad", Beverage:"Beverage" },
    ar: { Appetizer:"مقبلات", Soup:"شوربة", Main:"طبق رئيسي", Side:"طبق جانبي",
          Dessert:"حلوى", Salad:"سلطة", Beverage:"مشروب" },
  },

  /* ── Ride / task status ── */
  rideStatus: {
    en: { scheduled:"Scheduled", completed:"Completed", cancelled:"Cancelled", pending:"Pending" },
    ar: { scheduled:"مجدولة", completed:"مكتملة", cancelled:"ملغاة", pending:"قيد الانتظار" },
  },

  /* ── Common menu cuisine / dietary preference defaults ── */
  cuisine: {
    en: { Saudi:"Saudi", Lebanese:"Lebanese", Mediterranean:"Mediterranean", Yemeni:"Yemeni",
          Egyptian:"Egyptian", Moroccan:"Moroccan", Asian:"Asian", Indian:"Indian",
          Turkish:"Turkish", Italian:"Italian", Mexican:"Mexican", Western:"Western",
          Healthy:"Healthy", "High Protein":"High Protein", "Low Carb":"Low Carb",
          "Low Calorie":"Low Calorie", Vegetarian:"Vegetarian", Vegan:"Vegan",
          "Gluten-Free":"Gluten-Free", Light:"Light", Hearty:"Hearty", Quick:"Quick",
          "Family Friendly":"Family Friendly", Spicy:"Spicy", Mild:"Mild",
          Grilled:"Grilled", Baked:"Baked", Fried:"Fried" },
    ar: { Saudi:"سعودي", Lebanese:"لبناني", Mediterranean:"متوسطي", Yemeni:"يمني",
          Egyptian:"مصري", Moroccan:"مغربي", Asian:"آسيوي", Indian:"هندي",
          Turkish:"تركي", Italian:"إيطالي", Mexican:"مكسيكي", Western:"غربي",
          Healthy:"صحي", "High Protein":"بروتين عالي", "Low Carb":"كربوهيدرات منخفضة",
          "Low Calorie":"سعرات منخفضة", Vegetarian:"نباتي", Vegan:"نباتي صرف",
          "Gluten-Free":"خالٍ من الجلوتين", Light:"خفيف", Hearty:"دسم", Quick:"سريع",
          "Family Friendly":"مناسب للعائلة", Spicy:"حار", Mild:"مخفّف",
          Grilled:"مشوي", Baked:"مخبوز", Fried:"مقلي" },
  },
};

/* Title-case a snake_case key as last-resort label. */
function titleCase(key) {
  return String(key||"")
    .split(/[_\s-]/)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/* enumLabel(value, type, lang) — resolve a stable enum key to its
   localized label. Falls back to the EN map, then a title-cased
   version of the key. Safe to call with unknown values.
   Pass an array as `value` to get a comma-joined label list. */
function enumLabel(value, type, lang) {
  if (Array.isArray(value)) return value.map(v => enumLabel(v, type, lang)).join(", ");
  if (value === null || value === undefined || value === "") return "";
  const map = ENUM_LABELS[type];
  if (!map) return String(value);
  const dir = lang === "ar" ? "ar" : "en";
  return map[dir]?.[value] || map.en?.[value] || titleCase(value);
}

export { ENUM_LABELS, enumLabel };
